import { Injectable } from '@angular/core';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { EReferralReadinessObject } from '@libs/types/src/services/object-type/csez/ereferral-readiness.object';
import { EReferralUpdateDraftInput } from '@libs/types/src/services/input-type/csez/ereferral-update-draft.input';
import { EReferralConnectionObject } from '@libs/types/src/services/object-type/service/ereferral-connection.object';
import { ServiceSettingsUpdateInput } from '@libs/types/src/services/input-type/services/service-settings.update-input';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { GridInput } from '@it2go/data-grid';
import { EReferralCreateInput } from '@libs/types/src/services/input-type/csez/ereferral-create.input';
import { EReferralGridObject, EReferralObject } from '@libs/types/src/services/object-type/csez/ereferral.object';
import { filterFrag } from './types/filter.types';
import {
    EReferralRecipientSearchInput,
} from '@libs/types/src/services/input-type/csez/ereferral-recipient-search.input';
import { EReferralRecipientObject } from '@libs/types/src/services/object-type/csez/ereferral-recipient.object';
import { SnomedExaminationObject } from '@libs/types/src/list/object-type/snomed-examination.object';
import { CsezCodeListItemGridObject } from '@libs/types/src/list/object-type/csez-catalog.object';

@Injectable({ providedIn: 'root' })
export class EReferralGqlService extends GqlServiceV2 {
    public verifyEreferrals(input: ServiceSettingsUpdateInput): Promise<EReferralConnectionObject> {
        return this.mutate<EReferralConnectionObject>(`
            mutation($input: ServiceSettingsUpdateInput!) {
                ereferrals {
                    verify(input: $input) {
                        status
                        checkedAt
                        doctors { userId status }
                    }
                }
            }
        `, { input });
    }

    public getEReferrals(input: GridInput, fragment: string): Promise<EReferralGridObject> {
        return this.query<EReferralGridObject>(`
            query($input: GridInput!) {
                ereferrals {
                    filter(input: $input) {
                        ${filterFrag}
                        items { ${fragment} }
                    }
                }
            }
        `, { input });
    }

    public createEReferralDraft(input: EReferralCreateInput): Promise<EReferralObject> {
        return this.mutate<EReferralObject>(`
            mutation($input: EReferralCreateInput!) {
                ereferrals {
                    createDraft(input: $input) {
                        id
                    }
                }
            }
        `, { input });
    }

    public createEReferral(input: EReferralCreateInput): Promise<EReferralObject> {
        return this.mutate<EReferralObject>(`
            mutation($input: EReferralCreateInput!) {
                ereferrals {
                    create(input: $input) {
                        id
                    }
                }
            }
        `, { input });
    }

    public updateEReferralDraft(input: EReferralUpdateDraftInput): Promise<EReferralObject> {
        return this.mutate<EReferralObject>(`
            mutation($input: EReferralUpdateDraftInput!) {
                ereferrals {
                    updateDraft(input: $input) {
                        id
                    }
                }
            }
        `, { input });
    }

    public checkEReferralReadiness(input: IdInput): Promise<EReferralReadinessObject> {
        return this.query<EReferralReadinessObject>(`
            query($input: IdInput!) {
                ereferrals {
                    checkReadiness(input: $input) {
                        ready
                        settings
                        certificate
                        doctor
                        icp
                    }
                }
            }
        `, { input }, { offline: false });
    }

    public refreshEReferralStatus(input: IdInput, fragment: string): Promise<EReferralObject> {
        return this.mutate<EReferralObject>(`
            mutation($input: IdInput!) {
                ereferrals {
                    refreshStatus(input: $input) {
                        ${fragment}
                    }
                }
            }
        `, { input }, { offline: false });
    }

    public searchEReferralRecipients(input: EReferralRecipientSearchInput, fragment: string): Promise<EReferralRecipientObject[]> {
        return this.query<EReferralRecipientObject[]>(`
            query($input: EReferralRecipientSearchInput!) {
                ereferrals {
                    searchRecipients(input: $input) {
                        ${fragment}
                    }
                }
            }
        `, { input }, { offline: false });
    }

    public getEReferralExaminations(fragment: string): Promise<SnomedExaminationObject[]> {
        return this.query<SnomedExaminationObject[]>(`
            query {
                csezCatalog {
                    imagingExaminations {
                        ${fragment}
                    }
                }
            }
        `, {}, { offline: false });
    }

    public getCsezCodeListItems(input: GridInput, fragment: string): Promise<CsezCodeListItemGridObject> {
        return this.query<CsezCodeListItemGridObject>(`
            query($input: GridInput!) {
                csezCatalog {
                    codeListItems(input: $input) {
                        ${filterFrag}
                        items { ${fragment} }
                    }
                }
            }
        `, { input });
    }
}
